/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Sản phẩm & giá › Danh mục sản phẩm › Giá theo hạng and Khuyến mãi dịch vụ (ADR-026 §3.2–3.3): the
 * plan price rules of the tier books and of customers' contract books (a discount off the tier or
 * standard price for a group, a product or a plan, or a fixed price for one plan and cycle) and what each promotion code applies to in
 * the catalogue. Reading needs `catalog.read`; the forms need `pricing.manage` (the server says
 * whether the user holds it, `can.price`).
 */
import { CirclePlus, CircleStop, Target } from 'lucide-react';
import { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { CatalogScope, CycleCode, PlanRule, PromotionAction, PromotionScopeKind, RuleBook, RuleType, ServicePromotion } from '../../api/areas/catalog';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite } from '../../components/Write';
import { CATALOG_SCOPES, CYCLES, formatVnd, PROMOTION_ACTIONS, PROMOTION_SCOPES, promotionScopeBlocked, RULE_TYPES, ruleValue } from '../../lib/catalog';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, selectClass, type DialogBodyProps } from '../operator/shared';
import { CatalogCheck, CatalogField, CheckList, FormShell, useCatalogT } from './editorKit';

const iso = (local: string): string | null => (local ? new Date(local).toISOString() : null);

/** The catalogue's groups, products and plans to pick what a rule or a promotion applies to. */
function useTargets() {
  const groups = useApi((signal) => api.listCatalogGroups({ signal }), []);
  const products = useApi((signal) => api.listCatalogProducts(null, { signal }), []);
  const plans = useApi((signal) => api.listCatalogPlans(null, { signal }), []);
  const { en, name } = useCatalogT();
  return (scope: CatalogScope): { value: string; label: string }[] => {
    if (scope === 'GROUP') return groups.status === 'success' ? groups.data.groups.map((g) => ({ value: g.code, label: name(g) })) : [];
    if (scope === 'PRODUCT') return products.status === 'success' ? products.data.products.map((p) => ({ value: p.id, label: name(p) })) : [];
    return plans.status === 'success' ? plans.data.plans.map((p) => ({ value: p.id, label: `${en ? p.product_name_en : p.product_name_vi} · ${en ? p.name_en : p.name_vi}` })) : [];
  };
}

function TargetSelect({ field, scope, value, onChange }: { field: 'ruleTarget' | 'promoTarget'; scope: CatalogScope; value: string; onChange: (v: string) => void }) {
  const { t } = useCatalogT();
  const targets = useTargets()(scope);
  return (
    <CatalogField name={field}>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">{t('editor.none')}</option>
        {targets.map((x) => (
          <option key={x.value} value={x.value}>
            {x.label}
          </option>
        ))}
      </select>
    </CatalogField>
  );
}

function bookLabel(t: ReturnType<typeof useCatalogT>['t'], b: Pick<RuleBook, 'code' | 'applies_to' | 'customer_name'>): string {
  return b.customer_name ? `${b.code} · ${t('rules.contract', { name: b.customer_name })}` : `${b.code} · ${t(`prices.bookKind.${b.applies_to}`)}`;
}

// ---------------------------------------------------------------------------------------------
// Giá theo hạng

/** `/products?tab=product-catalog&sub=price-rules` */
export function PriceRulesPage() {
  const { t, en } = useCatalogT();
  useDocumentTitle(t('rules.title'));
  const [book, setBook] = useState('');
  const [all, setAll] = useState(false);
  const result = useApi((signal) => api.listPlanRules(book || null, all, { signal }), [book, all]);
  const books = result.status === 'success' ? result.data.price_books : [];
  const canPrice = result.status === 'success' && result.data.can.price;
  const target = (r: PlanRule) => (en ? r.scope_name_en : r.scope_name_vi) ?? r.scope_ref;
  const columns: Column<PlanRule>[] = [
    { key: 'book', header: t('rules.col.book'), cell: (r) => <span className="font-mono text-xs">{r.price_book_code}</span> },
    {
      key: 'target',
      header: t('rules.col.appliesTo'),
      cell: (r) => (
        <>
          <span className="font-medium">{target(r)}</span>
          <span className="block text-xs text-muted-foreground">{t(`rules.scope.${r.scope}`)}</span>
        </>
      ),
    },
    { key: 'cycle', header: t('rules.col.cycle'), cell: (r) => (r.cycle ? t(`cycle.${r.cycle}`) : t('editor.anyCycle')) },
    { key: 'rule', header: t('rules.col.rule'), cell: (r) => (r.rule_type === 'DISCOUNT_PERCENT' ? t('rules.discount', { value: Number(r.value) }) : t('rules.fixed', { amount: formatVnd(r.value) })) },
    { key: 'priority', header: t('rules.col.priority'), numeric: true, cell: (r) => r.priority },
    {
      key: 'when',
      header: t('rules.col.when'),
      className: 'whitespace-nowrap text-xs',
      cell: (r) => (
        <>
          {t('rules.since', { date: formatDateTime(r.effective_from) })}
          {r.effective_until ? <span className="block">{t('rules.until', { date: formatDateTime(r.effective_until) })}</span> : null}
        </>
      ),
    },
    { key: 'reason', header: t('rules.col.reason'), cell: (r) => <span className="text-xs">{r.reason}</span> },
    {
      key: 'end',
      header: '',
      cell: (r) =>
        canPrice && r.effective_until === null ? (
          <ActionDialog label={t('rules.end')} icon={CircleStop} title={t('rules.endTitle', { target: target(r) })} onChanged={result.reload}>
            {(p) => <EndRuleForm {...p} rule={r} />}
          </ActionDialog>
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title={t('rules.title')}
        crumbs={[{ label: t('crumb') }]}
        subtitle={t('rules.subtitle')}
        actions={
          canPrice ? (
            <ActionDialog label={t('rules.add')} icon={CirclePlus} title={t('rules.addTitle')} variant="default" wide onChanged={result.reload}>
              {(p) => <RuleForm {...p} books={books} defaultBook={book} />}
            </ActionDialog>
          ) : null
        }
      />
      <FilterBar>
        <Field label={t('rules.book')}>
          <select className={selectClass} value={book} onChange={(e) => setBook(e.target.value)}>
            <option value="">{t('rules.allBooks')}</option>
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {bookLabel(t, b)}
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          {t('rules.showClosed')}
        </label>
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? <Loading rows={4} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {result.status === 'success' ? <DataTable columns={columns} rows={result.data.rules} rowKey={(r) => r.id} empty={<EmptyState title={t('rules.empty')} />} /> : null}
      </Section>
    </>
  );
}

function RuleForm({ close, changed, books, defaultBook }: DialogBodyProps & { books: readonly RuleBook[]; defaultBook: string }) {
  const { t } = useCatalogT();
  // The STANDARD tier's book is the base the rules of every other book discount.
  const sellable = books.filter((b) => !(b.applies_to === 'TIER' && b.tier === 'STANDARD'));
  const [book, setBook] = useState(defaultBook || sellable[0]?.id || books[0]?.id || '');
  const [scope, setScope] = useState<CatalogScope>('GROUP');
  const [ref, setRef] = useState('');
  const [cycle, setCycle] = useState<CycleCode | ''>('');
  const [type, setType] = useState<RuleType>('DISCOUNT_PERCENT');
  const [value, setValue] = useState('');
  const [priority, setPriority] = useState('100');
  const [from, setFrom] = useState('');
  // "Now" is fixed at the first submit, so a retry after a lost answer is the same request.
  const now = useRef<string | null>(null);
  const w = useWrite((b: Parameters<typeof api.publishPlanRule>[0], opts) => api.publishPlanRule(b, opts));
  const parsed = value.trim() ? ruleValue(type, value) : null;
  const fixedWrong = type === 'FIXED_PRICE' && (scope !== 'PLAN' || !cycle);
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!book || !ref || parsed === null || fixedWrong}
      onSubmit={(reason) => {
        now.current ??= new Date().toISOString();
        void w
          .run({ price_book_id: book, scope, scope_ref: ref, cycle: cycle || null, rule_type: type, value: parsed ?? '0', priority: Number(priority || '100'), effective_from: iso(from) ?? now.current, reason })
          .then((r) => r && changed());
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <CatalogField name="ruleBook">
          <select className={selectClass} value={book} onChange={(e) => setBook(e.target.value)}>
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {bookLabel(t, b)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="ruleType">
          <select className={selectClass} value={type} onChange={(e) => setType(e.target.value as RuleType)}>
            {RULE_TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`rules.type.${x}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="ruleScope">
          <select
            className={selectClass}
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as CatalogScope);
              setRef('');
            }}
          >
            {CATALOG_SCOPES.map((x) => (
              <option key={x} value={x}>
                {t(`rules.scope.${x}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <TargetSelect field="ruleTarget" scope={scope} value={ref} onChange={setRef} />
        <CatalogField name="ruleCycle">
          <select className={selectClass} value={cycle} onChange={(e) => setCycle(e.target.value as CycleCode | '')}>
            <option value="">{t('editor.anyCycle')}</option>
            {CYCLES.filter((c) => c !== 'H1').map((c) => (
              <option key={c} value={c}>
                {t(`cycle.${c}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name={type === 'DISCOUNT_PERCENT' ? 'ruleValuePercent' : 'ruleValueAmount'}>
          <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} required />
        </CatalogField>
        <CatalogField name="rulePriority">
          <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} className="w-28" />
        </CatalogField>
        <CatalogField name="ruleFrom">
          <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </CatalogField>
      </div>
      {fixedWrong ? <Callout tone="warn">{t('rules.fixedNeedsPlan')}</Callout> : null}
      {value.trim() && parsed === null ? <Callout tone="warn">{t('editor.unreadable')}</Callout> : null}
    </FormShell>
  );
}

function EndRuleForm({ close, changed, rule }: DialogBodyProps & { rule: PlanRule }) {
  const [at, setAt] = useState('');
  const w = useWrite((b: { effective_until: string | null; reason: string }, opts) => api.endPlanRule(rule.id, b, opts));
  return (
    <FormShell write={w} close={close} onSubmit={(reason) => void w.run({ effective_until: iso(at), reason }).then((r) => r && changed())}>
      <CatalogField name="ruleEnd">
        <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
      </CatalogField>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Khuyến mãi dịch vụ

/** `/products?tab=product-catalog&sub=promotion-scopes` */
export function PromotionScopesPage() {
  const { t, en } = useCatalogT();
  useDocumentTitle(t('promoScope.title'));
  const result = useApi((signal) => api.listServicePromotions({ signal }), []);
  const canPrice = result.status === 'success' && result.data.can.price;
  const columns: Column<ServicePromotion>[] = [
    {
      key: 'code',
      header: t('promoScope.col.code'),
      cell: (p) => (
        <>
          <span className="font-mono font-medium">{p.code}</span>
          {p.disabled_at ? <Badge tone="neutral">{t('promoScope.disabled')}</Badge> : null}
        </>
      ),
    },
    { key: 'discount', header: t('promoScope.col.discount'), cell: (p) => (p.discount_type === 'PERCENT' ? t('promoScope.percent', { value: Number(p.value) }) : t('promoScope.fixed', { amount: formatVnd(p.value) })) },
    { key: 'valid', header: t('promoScope.col.valid'), className: 'whitespace-nowrap text-xs', cell: (p) => `${formatDateTime(p.starts_at)} – ${formatDateTime(p.ends_at)}` },
    { key: 'used', header: t('promoScope.col.used'), cell: (p) => t('promoScope.used', { count: p.redemptions, max: p.max_redemptions ?? t('promoScope.unlimited') }) },
    {
      key: 'scope',
      header: t('promoScope.col.appliesTo'),
      cell: (p) => (
        <>
          <span className="font-medium">{p.scope === 'TLD' ? (p.tld ? `.${p.tld}` : t('promoScope.scope.TLD')) : ((en ? p.scope_name_en : p.scope_name_vi) ?? p.scope_ref)}</span>
          <span className="block text-xs text-muted-foreground">{t(`promoScope.scope.${p.scope}`)}</span>
        </>
      ),
    },
    {
      key: 'terms',
      header: t('promoScope.col.terms'),
      cell: (p) =>
        p.scope === 'TLD' ? null : (
          <span className="text-xs text-muted-foreground">
            {[
              p.cycles ? p.cycles.map((c) => t(`cycle.${c}`)).join(', ') : t('promoScope.everyCycle'),
              p.applies_to ? p.applies_to.map((a) => t(`promoScope.action.${a}`)).join(', ') : t('promoScope.newOnly'),
              p.recurring ? t('promoScope.recurring') : t('promoScope.firstOnly'),
              p.once_per_customer ? t('promoScope.oncePerCustomer') : null,
              p.include_setup ? t('promoScope.includeSetup') : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ),
    },
    {
      key: 'edit',
      header: '',
      cell: (p) => {
        if (!canPrice) return null;
        const blocked = promotionScopeBlocked(p);
        return blocked ? (
          <span className="text-xs text-muted-foreground">{t(`promoScope.blocked.${blocked}`)}</span>
        ) : (
          <ActionDialog label={t('promoScope.edit')} icon={Target} title={t('promoScope.editTitle', { code: p.code })} wide onChanged={result.reload}>
            {(d) => <PromotionScopeForm {...d} promotion={p} />}
          </ActionDialog>
        );
      },
    },
  ];
  return (
    <>
      <PageHeader title={t('promoScope.title')} crumbs={[{ label: t('crumb') }]} subtitle={t('promoScope.subtitle')} />
      <Section flush>
        {result.status === 'loading' ? <Loading rows={4} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {result.status === 'success' ? <DataTable columns={columns} rows={result.data.promotions} rowKey={(p) => p.id} empty={<EmptyState title={t('promoScope.empty')} />} /> : null}
      </Section>
    </>
  );
}

function PromotionScopeForm({ close, changed, promotion: p }: DialogBodyProps & { promotion: ServicePromotion }) {
  const { t } = useCatalogT();
  const [scope, setScope] = useState<PromotionScopeKind>(p.scope === 'TLD' ? 'GROUP' : p.scope);
  const [ref, setRef] = useState(p.scope_ref ?? '');
  const [cycles, setCycles] = useState<CycleCode[]>(p.cycles ?? []);
  const [actions, setActions] = useState<PromotionAction[]>(p.applies_to ?? []);
  const [recurring, setRecurring] = useState(p.recurring);
  const [once, setOnce] = useState(p.once_per_customer);
  const [setup, setSetup] = useState(p.include_setup);
  const w = useWrite((b: Parameters<typeof api.setPromotionScope>[1], opts) => api.setPromotionScope(p.id, b, opts));
  const catalogue = scope !== 'TLD';
  return (
    <FormShell
      write={w}
      close={close}
      disabled={catalogue && !ref}
      onSubmit={(reason) =>
        void w
          .run(
            catalogue
              ? { scope, scope_ref: ref, cycles: cycles.length ? cycles : null, applies_to: actions.length ? actions : null, recurring, once_per_customer: once, include_setup: setup, reason }
              : { scope: 'TLD', scope_ref: null, cycles: null, applies_to: null, recurring: false, once_per_customer: false, include_setup: false, reason },
          )
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <CatalogField name="promoScope">
          <select
            className={selectClass}
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as PromotionScopeKind);
              setRef('');
            }}
          >
            {PROMOTION_SCOPES.map((x) => (
              <option key={x} value={x}>
                {t(`promoScope.scope.${x}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        {catalogue ? <TargetSelect field="promoTarget" scope={scope} value={ref} onChange={setRef} /> : null}
      </div>
      {catalogue ? (
        <>
          <CheckList name="promoCycles" options={CYCLES.filter((c) => c !== 'H1')} value={cycles} onChange={setCycles} label={(c) => t(`cycle.${c}`)} />
          <CheckList name="promoActions" options={PROMOTION_ACTIONS} value={actions} onChange={setActions} label={(a) => t(`promoScope.action.${a}`)} />
          <CatalogCheck name="promoRecurring" checked={recurring} onChange={setRecurring} />
          <CatalogCheck name="promoOncePerCustomer" checked={once} onChange={setOnce} />
          <CatalogCheck name="promoIncludeSetup" checked={setup} onChange={setSetup} />
        </>
      ) : null}
    </FormShell>
  );
}
